import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormFeedback,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import ProcessingSpinner from '../../../../ProcessingSpinner';

import { addFlashMessage } from '../../../../../helpers/FlashMessages';

import ErrorBoundary from '../../../../ErrorBoundary';

const ImageEditorPresetEditModal = ({
  handleClose, handleChange, presets, updatePresets, presetIndex,
}) => {
  const intl = useIntl();

  const [label, setLabel] = useState('');
  const [x, setX] = useState(1);
  const [y, setY] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (presetIndex !== null) {
      const preset = presets[presetIndex];
      setLabel(preset.label);
      setX(preset.x);
      setY(preset.y);
    }
  }, [presetIndex, presets]);

  const handleDimensions = (e, dimensionType) => {
    const { value } = e.target;

    if (dimensionType === 'x') setX(value);
    if (dimensionType === 'y') setY(value);
  };

  const handleUpdate = () => {
    setError(null);
    if (!x || !y) {
      setError(intl.formatMessage({
        defaultMessage: 'Please provide valid numbers for width and height',
      }));
      return;
    }

    setSaving(true);

    let newPresets;
    if (presetIndex !== null) {
      newPresets = presets.map((p, i) => {
        if (i !== presetIndex) return p;

        return {
          label, x: Number(x), y: Number(y),
        };
      });
    } else {
      newPresets = [...presets, {
        label, x: Number(x), y: Number(y),
      }];
    }
    handleChange('image_editor_presets', newPresets);
    updatePresets({ image_editor_presets: newPresets }).then(() => {
      setSaving(false);
      addFlashMessage({
        type: 'success',
        text: intl.formatMessage({
          defaultMessage: 'New settings will be available in Image Editor after refresh.',
        }),
      });
      handleClose();
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleUpdate();
    }

    if (e.key === 'Escape') {
      handleClose();
    }
  };

  return (
    <ErrorBoundary>
      <ModalHeader toggle={handleClose} className="modal-header-center">
        {
          presetIndex ?
            intl.formatMessage({ defaultMessage: 'Edit preset' }) :
            intl.formatMessage({ defaultMessage: 'Add new preset' })
        }
      </ModalHeader>
      <ModalBody>
        <Form className="edit-size-form" onSubmit={(e) => { e.preventDefault(); handleUpdate(); }}>
          <FormGroup>
            <Label for="presetLabel">
              {intl.formatMessage({ defaultMessage: 'Preset name' })}
            </Label>
            <Input
              id="presetLabel"
              onChange={(e) => setLabel(e.target.value)}
              value={label}
              onKeyDown={handleKeyDown}
            />
          </FormGroup>
          <FormGroup row>
            <Col sm={6}>
              <Label for="sizeX">
                {intl.formatMessage({ defaultMessage: 'Width (px)' })}
              </Label>
              <Input
                type="number"
                id="sizeX"
                name="sizeX"
                min={0}
                disabled={saving}
                value={x}
                onChange={(e) => handleDimensions(e, 'x')}
                onKeyDown={handleKeyDown}
              />
            </Col>
            <Col sm={6}>
              <Label for="sizeY">
                {intl.formatMessage({ defaultMessage: 'Height (px)' })}
              </Label>
              <Input
                type="number"
                id="sizeY"
                name="sizeY"
                min={0}
                disabled={saving}
                value={y}
                onChange={(e) => handleDimensions(e, 'y')}
                onKeyDown={handleKeyDown}
              />
            </Col>
          </FormGroup>
          {error && (
          <FormFeedback style={{ display: 'block' }}>
            {error}
          </FormFeedback>
          )}
        </Form>
      </ModalBody>
      <ModalFooter>
        <div className="buttons-group">
          <Button
            color="secondary"
            disabled={saving}
            onClick={handleClose}
          >
            {intl.formatMessage({ defaultMessage: 'Cancel' })}
          </Button>
          <Button
            color="primary"
            disabled={saving}
            onClick={handleUpdate}
          >
            <ProcessingSpinner show={saving} />
            {intl.formatMessage({ defaultMessage: 'Save' })}
          </Button>
        </div>
      </ModalFooter>
    </ErrorBoundary>
  );
};

ImageEditorPresetEditModal.defaultProps = {
  presetIndex: null,
};

ImageEditorPresetEditModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  updatePresets: PropTypes.func.isRequired,
  presets: PropTypes.array.isRequired,
  presetIndex: PropTypes.number,
};

export default ImageEditorPresetEditModal;
